/**
 * AdminReservationsManagementTab
 * Onglet de gestion complète des réservations (admin full-access)
 * - Liste filtrée + paginée
 * - Drawer de détail enrichi
 * - Actions admin : annuler, changer statut, ajouter note, rembourser caution
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, IconButton, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Grid, Drawer, Divider, Avatar, CircularProgress,
  Alert, Tooltip, Badge, TablePagination, InputAdornment, Collapse,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Autocomplete, Stack, Card, CardContent, LinearProgress, List, ListItem,
  ListItemText, ListItemIcon, alpha, useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Cancel as CancelIcon,
  CheckCircle as ApproveIcon,
  Edit as EditIcon,
  Note as NoteIcon,
  Warning as WarningIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Block as BlockIcon,
  Undo as RefundIcon,
  History as HistoryIcon,
  CreditCard as PaymentIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import adminReservationService from '../../services/adminReservationService';
import { RESERVATION_STATUS_CONFIG, RESERVATION_STATUSES } from '../../constants/reservationStatuses';

dayjs.locale('fr');

// ─── Constantes ────────────────────────────────────────────────────────────
const PAGE_SIZES = [10, 25, 50, 100];

const ADMIN_ACTIONS = [
  { value: 'force_cancel',    label: 'Annulation forcée',        icon: <CancelIcon />,   color: 'error'   },
  { value: 'approve_payment', label: 'Approuver paiement',       icon: <ApproveIcon />,  color: 'success' },
  { value: 'refund_deposit',  label: 'Rembourser caution',       icon: <RefundIcon />,   color: 'warning' },
  { value: 'update_status',   label: 'Modifier statut',          icon: <EditIcon />,     color: 'info'    },
  { value: 'add_note',        label: 'Ajouter une note',         icon: <NoteIcon />,     color: 'inherit' },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: '',        label: 'Tous les paiements' },
  { value: 'paid',    label: 'Payé' },
  { value: 'pending', label: 'En attente' },
  { value: 'failed',  label: 'Échoué' },
  { value: 'refunded',label: 'Remboursé' },
];

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Date de création' },
  { value: 'start_date', label: 'Date début' },
  { value: 'end_date',   label: 'Date fin' },
  { value: 'status',     label: 'Statut' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmtDate = (d) => d ? dayjs(d).format('DD/MM/YYYY') : '—';
const fmtDatetime = (d) => d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—';
const fmtAmount = (n) => n != null ? `${Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}` : '—';

const StatusChip = ({ status, size = 'small' }) => {
  const cfg = RESERVATION_STATUS_CONFIG[status] || { label: status, color: '#999', bgColor: '#eee' };
  return (
    <Chip
      label={`${cfg.icon || ''} ${cfg.label}`}
      size={size}
      sx={{
        backgroundColor: cfg.bgColor,
        color: cfg.color,
        fontWeight: 600,
        fontSize: size === 'small' ? '0.72rem' : '0.85rem',
        border: `1px solid ${cfg.color}40`,
      }}
    />
  );
};

// ─── Composant principal ─────────────────────────────────────────────────────
const AdminReservationsManagementTab = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  // Fallback colors if theme is undefined
  const primaryMain = theme?.palette?.primary?.main || '#6200ee';
  const primaryDark = theme?.palette?.primary?.dark || '#4b00b5';
  // ── State liste ──
  const [rows, setRows]               = useState([]);
  const [pagination, setPagination]   = useState({ current_page: 1, total_pages: 1, total_items: 0, items_per_page: 25 });
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);

  // ── Filtres ──
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters]         = useState({
    search:        '',
    status:        '',
    paymentStatus: '',
    hasIssues:     false,
    startDate:     null,
    endDate:       null,
    sortBy:        'created_at',
    sortOrder:     'DESC',
  });
  const [page, setPage]               = useState(0);  // 0-indexed pour MUI TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedReservationId, setSelectedReservationId] = useState(null);

  // ── Drawer détail ──
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [detailData, setDetailData]   = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // ── Dialog actions ──
  const [actionDialog, setActionDialog] = useState({ open: false, action: null, reservation: null });
  const [actionNote, setActionNote]     = useState('');
  const [actionStatus, setActionStatus] = useState('');
  const [actionProcessing, setActionProcessing] = useState(false);
  const [actionResult, setActionResult] = useState(null);  // { success, message }

  // Debounce search
  const searchTimer = useRef(null);

  // ── Chargement liste ───────────────────────────────────────────────────────
  const loadReservations = useCallback(async (overridePage) => {
    setLoading(true);
    setError(null);
    try {
      const currentPage = (overridePage ?? page) + 1; // MUI 0-indexed → API 1-indexed
      const res = await adminReservationService.getReservationsList({
        page:          currentPage,
        limit:         rowsPerPage,
        sortBy:        filters.sortBy,
        sortOrder:     filters.sortOrder,
        search:        filters.search || undefined,
        status:        filters.status || undefined,
        paymentStatus: filters.paymentStatus || undefined,
        hasIssues:     filters.hasIssues || undefined,
        startDate:     filters.startDate ? dayjs(filters.startDate).format('YYYY-MM-DD') : undefined,
        endDate:       filters.endDate   ? dayjs(filters.endDate).format('YYYY-MM-DD')   : undefined,
      });
      if (res.success) {
        setRows(res.data.reservations || []);
        setPagination(res.data.pagination || {});
      } else {
        setError(res.message || 'Erreur lors du chargement');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => { loadReservations(); }, [page, rowsPerPage]);

  // Déclencher reload filtre (reset page → 0)
  const applyFilters = () => {
    setPage(0);
    loadReservations(0);
  };

  const resetFilters = () => {
    setFilters({
      search: '', status: '', paymentStatus: '', hasIssues: false,
      startDate: null, endDate: null, sortBy: 'created_at', sortOrder: 'DESC',
    });
    setPage(0);
  };

  // Search debounced
  const handleSearchChange = (val) => {
    setFilters(f => ({ ...f, search: val }));
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(0);
      loadReservations(0);
    }, 500);
  };

  // ── Chargement détail ─────────────────────────────────────────────────────
  const openDetail = async (id) => {
    setDrawerOpen(true);
    setDetailData(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const res = await adminReservationService.getReservationDetail(id);
      if (res.success) setDetailData(res.data);
      else setDetailError(res.message || 'Erreur');
    } catch (e) {
      setDetailError(e?.response?.data?.message || e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Actions admin ─────────────────────────────────────────────────────────
  const openActionDialog = (action, reservation) => {
    setActionDialog({ open: true, action, reservation });
    setActionNote('');
    setActionStatus(reservation.status);
    setActionResult(null);
  };

  const closeActionDialog = () => {
    setActionDialog({ open: false, action: null, reservation: null });
    setActionResult(null);
  };

  const confirmAction = async () => {
    const { action, reservation } = actionDialog;
    if (!action || !reservation) return;
    setActionProcessing(true);
    try {
      const data = {};
      if (action === 'add_note')      data.note   = actionNote;
      if (action === 'force_cancel')  data.reason = actionNote;
      if (action === 'update_status') data.status = actionStatus;

      const res = await adminReservationService.performAction(reservation.id, action, data);
      if (res.success) {
        setActionResult({ success: true, message: res.message || 'Action effectuée avec succès' });
        await loadReservations();
        // Rafraîchir le détail si ouvert
        if (drawerOpen && detailData?.id === reservation.id) {
          openDetail(reservation.id);
        }
        setTimeout(closeActionDialog, 1500);
      } else {
        setActionResult({ success: false, message: res.message || 'Erreur lors de l\'action' });
      }
    } catch (e) {
      setActionResult({ success: false, message: e?.response?.data?.message || e.message });
    } finally {
      setActionProcessing(false);
    }
  };

  // ── UI helpers ────────────────────────────────────────────────────────────
  const activeFiltersCount = [
    filters.search, filters.status, filters.paymentStatus,
    filters.hasIssues, filters.startDate, filters.endDate,
  ].filter(Boolean).length;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
      <Box>

        {/* ── Barre de contrôle ─────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          {/* Recherche */}
          <TextField
            size="small"
            placeholder="Chercher par nom, email, ref…"
            value={filters.search}
            onChange={e => handleSearchChange(e.target.value)}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
          />

          {/* Filtre statut rapide */}
          <FormControl size="small" sx={{ minWidth: 155 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              label="Statut"
              value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            >
              <MenuItem value="">Tous</MenuItem>
              {Object.entries(RESERVATION_STATUS_CONFIG).map(([s, cfg]) => (
                <MenuItem key={s} value={s}>
                  {cfg.icon} {cfg.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Filtres avancés collapse button */}
          <Badge badgeContent={activeFiltersCount} color="primary">
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterIcon />}
              endIcon={filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setFiltersOpen(o => !o)}
            >
              Filtres avancés
            </Button>
          </Badge>

          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => loadReservations()}
          >
            Actualiser
          </Button>

          {/* Compteur */}
          {!loading && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              <strong>{pagination.total_items?.toLocaleString('fr-FR')}</strong> réservations
            </Typography>
          )}
        </Box>

        {/* ── Filtres avancés ────────────────────────────────────────────── */}
        <Collapse in={filtersOpen}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: alpha(primaryMain, 0.02) }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Date début"
                  value={filters.startDate}
                  onChange={v => setFilters(f => ({ ...f, startDate: v }))}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Date fin"
                  value={filters.endDate}
                  onChange={v => setFilters(f => ({ ...f, endDate: v }))}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Paiement</InputLabel>
                  <Select
                    label="Paiement"
                    value={filters.paymentStatus}
                    onChange={e => setFilters(f => ({ ...f, paymentStatus: e.target.value }))}
                  >
                    {PAYMENT_STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tri</InputLabel>
                  <Select
                    label="Tri"
                    value={filters.sortBy}
                    onChange={e => setFilters(f => ({ ...f, sortBy: e.target.value }))}
                  >
                    {SORT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ordre</InputLabel>
                  <Select
                    label="Ordre"
                    value={filters.sortOrder}
                    onChange={e => setFilters(f => ({ ...f, sortOrder: e.target.value }))}
                  >
                    <MenuItem value="DESC">Plus récent</MenuItem>
                    <MenuItem value="ASC">Plus ancien</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Problèmes</InputLabel>
                  <Select
                    label="Problèmes"
                    value={filters.hasIssues ? 'yes' : ''}
                    onChange={e => setFilters(f => ({ ...f, hasIssues: e.target.value === 'yes' }))}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="yes">Avec problèmes</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={resetFilters} startIcon={<RefreshIcon />}>
                    Réinitialiser
                  </Button>
                  <Button size="small" variant="contained" onClick={applyFilters} startIcon={<SearchIcon />}>
                    Appliquer
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        {/* ── Erreur ────────────────────────────────────────────────────── */}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* ── Tableau ───────────────────────────────────────────────────── */}
        {loading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: alpha(primaryMain, 0.06) } }}>
                <TableCell>#</TableCell>
                <TableCell>Référence</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Propriétaire</TableCell>
                <TableCell>Annonce</TableCell>
                <TableCell>Période</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Paiement</TableCell>
                <TableCell>Montant</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    Aucune réservation trouvée
                  </TableCell>
                </TableRow>
              )}

              {rows.map((r, idx) => {
                const hasIssue = r.issues && r.issues.length > 0;
                return (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{
                      backgroundColor: selectedReservationId === r.id
                        ? alpha('#ff9800', 0.12)
                        : hasIssue ? alpha('#68f621', 0.04) : undefined,
                      '&:last-child td': { borderBottom: 0 },
                    }}
                  >
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                      {(page * rowsPerPage) + idx + 1}
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {r.reference_number || r.id?.slice(0, 8)}
                      </Typography>
                      {hasIssue && (
                        <Tooltip title={r.issues.map(i => i.description || i.type || String(i)).join(' | ')}>
                          <WarningIcon fontSize="inherit" color="warning" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                        </Tooltip>
                      )}
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Avatar src={r.renter?.profilePicture} sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>
                          {r.renter?.name?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="caption" display="block" fontWeight={600}>
                            {r.renter?.name || '—'}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {r.renter?.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" fontWeight={600}>
                        {r.listing?.owner?.name || '—'}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ maxWidth: 160 }}>
                      <Typography variant="caption" noWrap>
                        {r.listing?.title || '—'}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="caption">
                        {fmtDate(r.start_date)} → {fmtDate(r.end_date)}
                      </Typography>
                      {r.duration_days && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {r.duration_days} jour{r.duration_days > 1 ? 's' : ''}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <StatusChip status={r.status} />
                    </TableCell>

                    <TableCell>
                      {r.payment_summary?.status ? (
                        <Chip
                          label={r.payment_summary.status}
                          size="small"
                          color={r.payment_summary.status === 'paid' ? 'success' : r.payment_summary.status === 'failed' ? 'error' : 'default'}
                          variant="outlined"
                        />
                      ) : '—'}
                    </TableCell>

                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {fmtAmount(r.payment_summary?.total_amount ?? r.total_amount)} {r.currency}
                    </TableCell>

                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
                        <Tooltip title="Voir le détail">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedReservationId(r.id);
                              window.open(`/reservations/${r.id}`, '_blank');
                            }}
                            color="primary"
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Annuler (force)">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={['cancelled','completed','refunded'].includes(r.status)}
                              onClick={() => openActionDialog('force_cancel', r)}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Modifier statut">
                          <IconButton size="small" color="info" onClick={() => openActionDialog('update_status', r)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ajouter note admin">
                          <IconButton size="small" onClick={() => openActionDialog('add_note', r)}>
                            <NoteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={pagination.total_items || 0}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={PAGE_SIZES}
            labelRowsPerPage="Lignes :"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count?.toLocaleString('fr-FR') || '?'}`}
          />
        </TableContainer>

        {/* ══ DRAWER DÉTAIL ═══════════════════════════════════════════════ */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { width: { xs: '100vw', sm: 520, md: 600 }, display: 'flex', flexDirection: 'column' } }}
        >
          {/* Header */}
          <Box sx={{
            px: 3, py: 2,
            background: `linear-gradient(135deg, ${primaryMain} 0%, ${primaryDark} 100%)`,
            color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>Détail de la réservation</Typography>
              {detailData && (
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {detailData.reference_number || detailData.id?.slice(0, 8)}
                </Typography>
              )}
            </Box>
            <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
            {detailLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                <CircularProgress />
              </Box>
            )}

            {detailError && <Alert severity="error">{detailError}</Alert>}

            {detailData && !detailLoading && (
              <Stack spacing={3}>
                {/* Statut + dates */}
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <StatusChip status={detailData.status} size="medium" />
                      <Typography variant="caption" color="text.secondary">
                        Créée le {fmtDatetime(detailData.created_at)}
                      </Typography>
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Début</Typography>
                        <Typography variant="body2" fontWeight={600}>{fmtDate(detailData.start_date)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Fin</Typography>
                        <Typography variant="body2" fontWeight={600}>{fmtDate(detailData.end_date)}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Client */}
                <DetailSection title="Client" icon={<PersonIcon />}>
                  <InfoRow label="Nom"    value={detailData.renter?.name} />
                  <InfoRow label="Email"  value={detailData.renter?.email} />
                  <InfoRow label="Tél."   value={detailData.renter?.phone || '—'} />
                  <InfoRow label="Vérifié" value={detailData.renter?.is_verified ? '✅ Oui' : '❌ Non'} />
                  {detailData.admin_insights?.user_history && (
                    <InfoRow
                      label="Historique"
                      value={`${detailData.admin_insights.user_history.total || 0} rés. au total`}
                    />
                  )}
                </DetailSection>

                {/* Annonce / Propriétaire */}
                <DetailSection title="Annonce & Propriétaire" icon={<StoreIcon />}>
                  <InfoRow label="Titre"        value={detailData.listing?.title} />
                  <InfoRow label="Propriétaire" value={detailData.listing?.owner?.name || '—'} />
                  <InfoRow label="Email prop."  value={detailData.listing?.owner?.email || '—'} />
                  {detailData.listing?.location && (
                    <InfoRow label="Lieu" value={detailData.listing.location} />
                  )}
                </DetailSection>

                {/* Paiements */}
                {detailData.payments?.length > 0 && (
                  <DetailSection title="Paiements" icon={<PaymentIcon />}>
                    {detailData.payments.map((p) => (
                      <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {p.payment_type} · {fmtDatetime(p.transaction_date || p.created_at)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip
                            label={p.status}
                            size="small"
                            color={p.status === 'paid' ? 'success' : p.status === 'failed' ? 'error' : 'default'}
                            variant="outlined"
                          />
                          <Typography variant="caption" fontWeight={700}>{fmtAmount(p.amount)}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </DetailSection>
                )}

                {/* Cautions */}
                {detailData.deposit && (
                  <DetailSection title="Cautions" icon={<MoneyIcon />}>
                    {[detailData.deposit].map((d) => (
                      <Box key={d.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                        <Box>
                          <Chip label={d.status} size="small" variant="outlined" />
                          {d.refund_date && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              Remb. le {fmtDate(d.refund_date)}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" fontWeight={700}>{fmtAmount(d.amount)}</Typography>
                      </Box>
                    ))}
                  </DetailSection>
                )}

                {/* Risques / problèmes */}
                {detailData.admin_insights?.risk_indicators?.length > 0 && (
                  <DetailSection title="Indicateurs de risque" icon={<WarningIcon />} color="warning.main">
                    {detailData.admin_insights.risk_indicators.map((r, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.4 }}>
                        <WarningIcon fontSize="small" color="warning" />
                        <Typography variant="caption">{r.description || r.type || String(r)}</Typography>
                      </Box>
                    ))}
                  </DetailSection>
                )}

                {/* Timeline */}
                {detailData.admin_insights?.timeline?.length > 0 && (
                  <DetailSection title="Historique" icon={<HistoryIcon />}>
                    {detailData.admin_insights.timeline.map((ev, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1, py: 0.4 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
                          {fmtDatetime(ev.timestamp || ev.date)}
                        </Typography>
                        <Typography variant="caption">{ev.event || ev.description || ev}</Typography>
                      </Box>
                    ))}
                  </DetailSection>
                )}

                {/* Actions disponibles depuis le détail */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Actions admin</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {ADMIN_ACTIONS.map(a => (
                      <Button
                        key={a.value}
                        size="small"
                        variant="outlined"
                        color={a.color}
                        startIcon={a.icon}
                        disabled={
                          a.value === 'force_cancel' &&
                          ['cancelled','completed','refunded'].includes(detailData.status)
                        }
                        onClick={() => openActionDialog(a.value, detailData)}
                      >
                        {a.label}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </Stack>
            )}
          </Box>
        </Drawer>

        {/* ══ DIALOG ACTION ADMIN ══════════════════════════════════════════ */}
        <Dialog open={actionDialog.open} onClose={closeActionDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {ADMIN_ACTIONS.find(a => a.value === actionDialog.action)?.label || 'Action admin'}
            <IconButton onClick={closeActionDialog} size="small"><CloseIcon /></IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {actionResult ? (
              <Alert severity={actionResult.success ? 'success' : 'error'} sx={{ mb: 1 }}>
                {actionResult.message}
              </Alert>
            ) : (
              <>
                <DialogContentText sx={{ mb: 2 }}>
                  Réservation : <strong>{actionDialog.reservation?.reference_number || actionDialog.reservation?.id?.slice(0, 8)}</strong>
                  {' — '}Client : <strong>{actionDialog.reservation?.renter?.name || '—'}</strong>
                </DialogContentText>

                {/* Champ note pour force_cancel / add_note */}
                {['force_cancel', 'add_note'].includes(actionDialog.action) && (
                  <TextField
                    label={actionDialog.action === 'force_cancel' ? 'Motif d\'annulation' : 'Note admin'}
                    fullWidth
                    multiline
                    rows={3}
                    value={actionNote}
                    onChange={e => setActionNote(e.target.value)}
                    required={actionDialog.action === 'add_note'}
                    placeholder={actionDialog.action === 'force_cancel' ? 'Expliquer la raison…' : 'Votre note interne…'}
                  />
                )}

                {/* Sélection statut */}
                {actionDialog.action === 'update_status' && (
                  <FormControl fullWidth>
                    <InputLabel>Nouveau statut</InputLabel>
                    <Select
                      label="Nouveau statut"
                      value={actionStatus}
                      onChange={e => setActionStatus(e.target.value)}
                    >
                      {Object.entries(RESERVATION_STATUS_CONFIG).map(([s, cfg]) => (
                        <MenuItem key={s} value={s}>{cfg.icon} {cfg.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Confirmation remboursement caution */}
                {actionDialog.action === 'refund_deposit' && (
                  <Alert severity="warning">
                    Cette action déclenchera le remboursement de la caution au client. Confirmer ?
                  </Alert>
                )}

                {/* Confirmation approbation paiement */}
                {actionDialog.action === 'approve_payment' && (
                  <Alert severity="info">
                    Marquer le paiement de cette réservation comme approuvé.
                  </Alert>
                )}
              </>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={closeActionDialog} disabled={actionProcessing}>Annuler</Button>
            {!actionResult && (
              <Button
                variant="contained"
                color={ADMIN_ACTIONS.find(a => a.value === actionDialog.action)?.color || 'primary'}
                onClick={confirmAction}
                disabled={actionProcessing || (actionDialog.action === 'add_note' && !actionNote.trim())}
                startIcon={actionProcessing ? <CircularProgress size={16} /> : null}
              >
                {actionProcessing ? 'Traitement…' : 'Confirmer'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

      </Box>
    </LocalizationProvider>
  );
};

// ─── Sous-composants helpers ─────────────────────────────────────────────────
const DetailSection = ({ title, icon, children, color = 'primary.main' }) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
      <Box sx={{ color, display: 'flex' }}>{icon}</Box>
      <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
    </Box>
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
      {children}
    </Paper>
  </Box>
);

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.35 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="caption" fontWeight={600}>{value || '—'}</Typography>
  </Box>
);

export default AdminReservationsManagementTab;
